import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  PauseIcon,
  PlayIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
} from '@heroicons/react/24/solid';
import { useRef } from 'react';
import 'cloudflare-video-element';
import clsx from 'clsx';
import {
  MediaActionTypes,
  MediaProvider,
  useMediaDispatch,
  useMediaRef,
  useMediaSelector,
} from 'media-chrome/react/media-store';
import YouTubeVideoElement from 'youtube-video-element';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'youtube-video': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        src?: string;
        slot?: string;
        crossorigin?: boolean;
        autoplay?: boolean;
        muted?: boolean;
      };
      'cloudflare-video': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        src?: string;
        slot?: string;
        crossorigin?: boolean;
        autoplay?: boolean;
        muted?: boolean;
      };
    }
  }
}

interface PlayerProps {
  video: string;
  autoplay?: boolean;
  platform: 'youtube' | 'vimeo' | 'cloudflare' | 'other';
  className?: string;
  muted?: boolean;
}

if (typeof window !== 'undefined') {
  if (!customElements.get('youtube-video')) {
    customElements.define('youtube-video', YouTubeVideoElement);
  }
}

const extractVimeoId = (url: string): string | null => {
  const match = /vimeo\.com\/(?:video\/)?(\d+)/.exec(url);
  return match ? match[1] : null;
};

const Video = ({ video, platform, autoplay = false, muted = true }: PlayerProps) => {
  const mediaRef = useMediaRef();

  return (
    <>
      {platform === 'youtube' && (
        <youtube-video
          ref={mediaRef as unknown as React.LegacyRef<HTMLElement>}
          src={video}
          slot='media'
          crossorigin
          autoplay={autoplay}
          muted={muted}
          style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
        />
      )}
      {platform === 'vimeo' &&
        (() => {
          const vimeoId = extractVimeoId(video);
          if (!vimeoId) {
            console.error('Invalid Vimeo URL');
            return null;
          }
          return (
            <iframe
              ref={mediaRef as unknown as React.LegacyRef<HTMLIFrameElement>}
              title='Vimeo Video'
              src={`https://player.vimeo.com/video/${vimeoId}?autoplay=${autoplay ? 1 : 0}&muted=${muted ? 1 : 0}`}
              allow='autoplay; fullscreen'
              style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
              allowFullScreen
            />
          );
        })()}
      {platform === 'cloudflare' && (
        <cloudflare-video
          ref={mediaRef as unknown as React.LegacyRef<HTMLElement>}
          src={video}
          slot='media'
          crossorigin
          autoplay={autoplay}
          muted={muted}
          style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
        />
      )}
      {platform === 'other' && (
        <video
          ref={mediaRef as unknown as React.LegacyRef<HTMLVideoElement>}
          src={video}
          slot='media'
          autoPlay={autoplay}
          muted={muted}
          style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
        />
      )}
    </>
  );
};

const PlayButton = () => {
  const dispatch = useMediaDispatch();
  const mediaPaused = useMediaSelector((state) => state.mediaPaused);

  return (
    <button
      type='button'
      onClick={() => {
        const type = mediaPaused ? MediaActionTypes.MEDIA_PLAY_REQUEST : MediaActionTypes.MEDIA_PAUSE_REQUEST;
        dispatch({ type });
      }}
      className={clsx(
        'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2',
        'rounded-full bg-black bg-opacity-50 border-none p-3 cursor-pointer',
        'flex items-center justify-center transition-opacity duration-300',
        'pointer-events-auto',
        mediaPaused ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
      )}
    >
      {mediaPaused ? <PlayIcon className='h-6 w-6 text-white' /> : <PauseIcon className='h-6 w-6 text-white' />}
    </button>
  );
};

const VolumeButton = () => {
  const dispatch = useMediaDispatch();
  const mediaVolume = useMediaSelector((state) => state.mediaVolume);
  const mediaMuted = useMediaSelector((state) => state.mediaMuted);

  const handleToggleMute = () => {
    dispatch({
      type: mediaMuted ? MediaActionTypes.MEDIA_UNMUTE_REQUEST : MediaActionTypes.MEDIA_MUTE_REQUEST,
    });
  };

  return (
    <button
      type='button'
      onClick={handleToggleMute}
      className={clsx(
        'absolute bottom-4 right-14 cursor-pointer',
        'pointer-events-auto',
        'transition-opacity duration-300',
        'opacity-0 group-hover:opacity-100',
        'p-1 rounded-full',
        'bg-black/50 shadow-lg',
      )}
    >
      {mediaMuted || mediaVolume === 0 ? (
        <SpeakerXMarkIcon className='h-6 w-6 text-white' />
      ) : (
        <SpeakerWaveIcon className='h-6 w-6 text-white' />
      )}
    </button>
  );
};

const FullscreenButton = () => {
  const dispatch = useMediaDispatch();
  const mediaIsFullscreen = useMediaSelector((state) => state.mediaIsFullscreen);

  const handleToggleFullscreen = () => {
    const type = mediaIsFullscreen
      ? MediaActionTypes.MEDIA_EXIT_FULLSCREEN_REQUEST
      : MediaActionTypes.MEDIA_ENTER_FULLSCREEN_REQUEST;
    dispatch({ type });
  };

  return (
    <button
      type='button'
      tabIndex={0}
      onClick={handleToggleFullscreen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleToggleFullscreen();
        }
      }}
      className={clsx(
        'absolute bottom-4 right-4 cursor-pointer',
        'pointer-events-auto',
        'transition-opacity duration-300',
        'opacity-0 group-hover:opacity-100',
        'p-1 rounded-full',
        'bg-black/50 shadow-lg',
      )}
    >
      {mediaIsFullscreen ? (
        <ArrowsPointingInIcon className='h-6 w-6 text-white' />
      ) : (
        <ArrowsPointingOutIcon className='h-6 w-6 text-white' />
      )}
    </button>
  );
};

const Player = ({ video, platform, autoplay, className, muted = true }: PlayerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <MediaProvider>
      <div
        ref={containerRef}
        className={clsx('relative w-full max-w-full mx-auto', 'before:block before:pb-[56.25%]', 'group', className)}
      >
        <div className='absolute inset-0'>
          {video && <Video video={video} platform={platform} autoplay={autoplay} muted={muted} />}

          <div className='absolute inset-0 pointer-events-none'>
            {platform !== 'vimeo' && <PlayButton />}
            <VolumeButton />
            <FullscreenButton />
          </div>
        </div>
      </div>
    </MediaProvider>
  );
};

export default Player;
